<template>
  <div class="container-fluid experiment-page">
    <div class="experiment-wrapper">
      <!-- Experiment Form Section -->
      <div class="experiment-form-section">
        <div id="experiment">
          <experiment-form :experiment="experiment" :view-only="viewOnly" :experiment-ended="experimentEnded"  @edit-successful="editSuccessful" @edit-fail="editFail" @edit-cancelled="editCancelled" v-if="experiment"></experiment-form>
          <div class="row" v-if="!experimentEnded">
            <button :class="`btn btn-${btnClass}`" style="margin:auto" @click="editExperiment" :disabled="experiment ? experiment.variants.length < 2 : true">
              <div v-if="isEditing">
                <span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                <span class="sr-only">Loading...</span>
              </div>
              <span v-else>
                {{ btnText }}
              </span>
            </button>
          </div>
        </div>
      </div>
      
      <!-- Image Upload Section -->
      <div class="image-upload-section">
        <div class="photo-uploader-container">
          <h5 class="uploader-title">
            <i class="fas fa-images"></i> Experiment UI Images
          </h5>

          <!-- Skip Images Checkbox -->
          <div class="skip-images-container" v-if="experiment && experiment.status === 'created'">
            <label class="skip-images-checkbox" :class="{ active: experiment.skip_images }">
              <input
                type="checkbox"
                :checked="experiment.skip_images"
                @change="toggleSkipImages"
                :disabled="updatingSkipImages"
              >
              <span class="skip-images-content">
                <span class="skip-images-label">Skip image requirement</span>
                <span class="skip-images-hint">For experiments without UI changes</span>
              </span>
              <span v-if="updatingSkipImages" class="skip-images-spinner">
                <i class="fas fa-spinner fa-spin"></i>
              </span>
            </label>
          </div>
          <div class="skip-images-info" v-else-if="experiment && experiment.skip_images">
            <i class="fas fa-info-circle"></i>
            <span>Image requirement is skipped for this experiment</span>
          </div>

          <div class="uploader-form">
            <div class="variant-select-group">
              <label class="variant-label">Select Variant to Upload (1 image per variant)</label>
              <select v-model="selectedVariantToUpload" class="variant-dropdown">
                <option v-for="variant in getVariants()" :key="variant._variant_name" :value="variant._variant_name">
                  Variant {{ variant._variant_name }}{{ variantHasImage(variant._variant_name) ? ' ✓ (uploaded)' : '' }}
                </option>
              </select>
            </div>
            
            <input
              type="file"
              class="file-input"
              id="file-input"
              accept="image/*"
              @change="handleFileChange"
            />
            <label for="file-input" class="file-label">
              <i class="fas fa-cloud-upload-alt"></i>
              {{ imageFile ? imageFile.file.name : "Click Here to Select Photo" }}
            </label>
            
            <button @click="uploadImage" class="upload-button" :disabled="uploading">
              <i :class="uploading ? 'fa-solid fa-sync fa-spin' :'fa-solid fa-upload'"></i>
              {{ uploading ? 'Uploading...' : 'Upload Image' }}
            </button>
          </div>
        </div>
        
        <!-- Uploaded Images Display -->
        <div class="uploaded-images-section" v-if="uiImages && uiImages.length > 0">
          <h5 class="uploaded-images-title">
            <i class="fas fa-check-circle"></i> Uploaded Images
          </h5>
          <div class="uploaded-images-grid">
            <div 
              class="experiment-ui" 
              v-for="uiImage in sortedUiImages" 
              :key="uiImage.url"
            >
              <button 
                class="delete-image-btn" 
                @click.stop="deleteImage(uiImage)"
                title="Delete image"
              >
                <i class="fas fa-times"></i>
              </button>
              <div class="image-container" @click="openLightbox(uiImage.url)">
                <label>Variant {{ uiImage.variant }}</label>
                <img :src="uiImage.url" :alt="uiImage.variant + '-image'" />
                <span class="click-hint"><i class="fas fa-expand"></i> Click to enlarge</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Lightbox Modal -->
    <div v-if="lightboxImage" class="lightbox-overlay" @click="closeLightbox">
      <div class="lightbox-content" @click.stop>
        <button v-show="lightboxLoaded" class="lightbox-close" @click="closeLightbox">
          <i class="fas fa-times"></i>
        </button>
        <img 
          :src="lightboxImage" 
          alt="Full size image" 
          @load="lightboxLoaded = true"
          :class="{ 'lightbox-img-loaded': lightboxLoaded }"
        />
        <div v-if="!lightboxLoaded" class="lightbox-loading">
          <i class="fas fa-spinner fa-spin"></i>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ExperimentForm from '../components/ExperimentForm.vue'
import { bucketService } from '../services/bucket.service';
import { convertImageToPngBase64 } from '../util/fileReader';
import { permissionsMixin } from '../mixins/permissions.mixin';
import notify from '../util/notification';

export default {
  mixins: [permissionsMixin],
  data () {
    return {
      isEditing: false,
      btnClass: "primary",
      btnText: "Edit Experiment",
      selectedVariantToUpload: "",
      imageFile: null,
      uiImages: [],
      uploading: false,
      lightboxImage: null,
      lightboxLoaded: false,
      updatingSkipImages: false
    }
  },
  computed: {
    experiment() {
      return this.$store.getters.experiments.find(item => item.id == this.$route.params.id)
    },
    viewOnly() {
      // Convert route param to boolean and handle experiment status check
      const isViewOnlyRoute = Boolean(this.$route.params.viewOnly);
      
      // Check if user can edit this specific experiment based on permissions
      if (this.experiment && !this.canEditThisExperiment(this.experiment)) {
        return true;
      }
      
      return isViewOnlyRoute;
    },
    experimentEnded() {
      return this.experiment && this.experiment.status == "ended"
    },
    sortedUiImages() {
      if (!this.uiImages || this.uiImages.length === 0) return [];
      return [...this.uiImages].sort((a, b) => {
        return (a.variant || '').localeCompare(b.variant || '');
      });
    },
  },
  methods: {
    getVariants(){
      return this.experiment !== undefined ? this.experiment.variants : []
    },
    editExperiment(){ 
      if (this.experiment.status != "ended") {
        this.isEditing = true
        this.btnClass = "primary"
        this.$emit('editExperiment')
      }else {
        alert('You can not edit an experiment that is already started.')
      }
    },
    editSuccessful(){
      this.isEditing = false
      this.btnClass = "success"
      this.btnText = "Edited!"
    },
    editFail(){
      this.isEditing = false
      this.btnClass = "danger"
      this.btnText = "Error!"
    },
    editCancelled(){
      this.isEditing = false
    },
    async handleFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        try {
          const base64 = await convertImageToPngBase64(file);
          this.imageFile = { file, base64 };
        } catch (error) {
          console.error('Error converting image to PNG:', error);
          notify('Failed to process image. Please try a different file.', 'error');
        }
      }
    },

    async fetchImages(){
      try {
        const imageArray = (await bucketService.getImages(this.experiment.id)).data
        this.uiImages = imageArray.data
      } catch (error) {
        console.error('Error fetching images:', error);
      } finally {
        if(this.uploading) this.uploading = false;
      }
    },
    openLightbox(imageUrl) {
      this.lightboxLoaded = false;
      this.lightboxImage = imageUrl;
      document.body.style.overflow = 'hidden';
    },
    closeLightbox() {
      this.lightboxImage = null;
      document.body.style.overflow = '';
    },
    variantHasImage(variantName) {
      return this.uiImages && this.uiImages.some(img => img.variant === variantName);
    },
    async uploadImage() {
      if (!this.imageFile) {
        notify('Please select an image file first.', 'error');
        return;
      }
      
      if (!this.selectedVariantToUpload) {
        notify('Please select a variant to upload the image for.', 'error');
        return;
      }

      if (this.variantHasImage(this.selectedVariantToUpload)) {
        notify('An image already exists for this variant. Please delete it first.', 'error');
        return;
      }
      
      this.uploading = true;
      
      try {
        const pngFileName = `${this.selectedVariantToUpload}.png`;
        const pngFile = { name: pngFileName, size: this.imageFile.file.size, type: 'image/png' };

        const res = await bucketService.uploadImage(
          pngFile, 
          this.imageFile.base64, 
          { experimentId: this.experiment.id, variant: this.selectedVariantToUpload }
        );
        
        if (res && res.status === 200) {
          notify('Image uploaded successfully!', 'success');
          await this.fetchImages();
          this.imageFile = null;
          const fileInput = document.getElementById('file-input');
          if (fileInput) fileInput.value = '';
          this.scrollToUploadedImages();
        }
      } catch (error) {
        console.error('Upload failed:', error);
        notify(error.message || 'Failed to upload image. Please try again.', 'error');
      } finally {
        this.uploading = false;
      }
    },
    async deleteImage(image) {
      if (!confirm(`Are you sure you want to delete the image for Variant ${image.variant}?`)) {
        return;
      }

      try {
        await bucketService.deleteImage(image.id, image.experimentId, image.name);
        notify('Image deleted successfully!', 'success');
        this.fetchImages();
      } catch (error) {
        console.error('Delete failed:', error);
        notify(error.message || 'Failed to delete image. Please try again.', 'error');
      }
    },
    async toggleSkipImages(event) {
      const newValue = event.target.checked;
      this.updatingSkipImages = true;

      try {
        const payload = {
          id: this.experiment.id,
          data: {
            ...this.experiment,
            skip_images: newValue
          }
        };

        await this.$store.dispatch('configEdit', { type: 'experiment', payload });
        notify(newValue ? 'Image requirement skipped' : 'Image requirement enabled', 'success');
      } catch (error) {
        console.error('Failed to update skip_images:', error);
        notify('Failed to update setting. Please try again.', 'error');
        // Revert the checkbox state on error
        event.target.checked = !newValue;
      } finally {
        this.updatingSkipImages = false;
      }
    },
    scrollToUploadedImages() {
      this.$nextTick(() => {
        const uploadedSection = document.querySelector('.uploaded-images-section');
        if (uploadedSection) {
          uploadedSection.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
      });
    }
  },
  watch: {
    experiment: {
      immediate: true,
      handler(newExperiment) {
        if (newExperiment?.id) {
          this.fetchImages();
          // Set initial variant to first variant
          if (newExperiment.variants && newExperiment.variants.length > 0) {
            this.selectedVariantToUpload = newExperiment.variants[0]._variant_name;
          }
        }
      },
    },
  },
  components: {
    ExperimentForm
  }
}
</script>

<style scoped>
.experiment-page {
  position: relative;
}

.experiment-wrapper {
  max-width: 1350px;
  margin: 0 auto;
}

#experiment {
  padding: 20px;
  border-radius: 10px;
  background-color: #ecf0f1;
}

.experiment-form-section {
  margin-bottom: 2rem;
}

.image-upload-section {
  margin-top: 1.5rem;
}

/* Photo Uploader Container */
.photo-uploader-container {
  background-color: #1a1a2e;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
}

.uploader-title {
  color: #fff;
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #3498db;
  display: flex;
  align-items: center;
  gap: 10px;
}

.uploader-title i {
  color: #3498db;
}

.uploader-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.variant-select-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.variant-label {
  color: #e0e0e0;
  font-weight: 500;
  font-size: 0.95rem;
}

.variant-dropdown {
  padding: 0.75rem 1rem;
  font-size: 1rem;
  border-radius: 8px;
  border: 2px solid #3498db;
  background-color: #16213e;
  color: #fff;
  cursor: pointer;
  transition: border-color 0.2s ease;
}

.variant-dropdown:focus {
  outline: none;
  border-color: #5dade2;
}

.variant-dropdown option {
  background-color: #16213e;
  color: #fff;
}

/* Hidden file input */
.file-input {
  display: none;
}

/* File label styled as a button */
.file-label {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background-color: #0f3460;
  color: #fff;
  font-size: 1rem;
  font-weight: 600;
  padding: 1rem 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
  border: 2px dashed #3498db;
}

.file-label:hover {
  background-color: #1a5276;
  border-color: #5dade2;
}

.file-label i {
  font-size: 1.2rem;
}

.upload-button {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  justify-content: center;
  background-color: #27ae60;
  color: #fff;
  font-size: 1rem;
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  align-self: flex-end;
}

.upload-button:hover:not(:disabled) {
  background-color: #2ecc71;
  transform: translateY(-2px);
}

.upload-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Uploaded Images Section */
.uploaded-images-section {
  margin-top: 1.5rem;
  background-color: #1a1a2e;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
}

.uploaded-images-title {
  color: #fff;
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #27ae60;
  display: flex;
  align-items: center;
  gap: 10px;
}

.uploaded-images-title i {
  color: #27ae60;
}

.uploaded-images-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 1rem;
}

.experiment-ui {
  position: relative;
  padding: 1rem;
  display: flex;
  justify-content: center;
  text-align: center;
  background-color: #2c3e50;
  border-radius: 8px;
  transition: all 0.2s ease;
  border: 2px solid transparent;
}

.experiment-ui:hover {
  border-color: #3498db;
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(52, 152, 219, 0.3);
}

.delete-image-btn {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #e74c3c;
  color: #fff;
  border: 2px solid #1a1a2e;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  transition: all 0.2s ease;
  z-index: 10;
}

.delete-image-btn:hover {
  background-color: #c0392b;
  transform: scale(1.1);
}

.image-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
}

.experiment-ui label {
  font-size: 0.9rem;
  color: #fff;
  font-weight: 600;
  margin-bottom: 0.5rem;
  pointer-events: none;
}

.image-container img {
  max-width: 100%;
  max-height: 120px;
  border-radius: 6px;
  object-fit: contain;
}

.click-hint {
  font-size: 0.75rem;
  color: #95a5a6;
  margin-top: 0.5rem;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.experiment-ui:hover .click-hint {
  opacity: 1;
}

/* Lightbox */
.lightbox-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  cursor: pointer;
}

.lightbox-content {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  cursor: default;
}

.lightbox-content img {
  max-width: 100%;
  max-height: 90vh;
  border-radius: 8px;
  box-shadow: 0 10px 50px rgba(0, 0, 0, 0.5);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.lightbox-content img.lightbox-img-loaded {
  opacity: 1;
}

.lightbox-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #fff;
  font-size: 2.5rem;
}

.lightbox-close {
  position: absolute;
  top: -40px;
  right: 0;
  background: none;
  border: none;
  color: #fff;
  font-size: 2rem;
  cursor: pointer;
  padding: 0.5rem;
  transition: color 0.2s ease;
  z-index: 1;
}

.lightbox-close:hover {
  color: #e74c3c;
}

/* Skip Images Checkbox in Image Upload Section */
.skip-images-container {
  margin-bottom: 1rem;
}

.skip-images-checkbox {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  background: #856404;
  border: 2px solid #ffc107;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.skip-images-checkbox:hover {
  background: #6d5303;
}

.skip-images-checkbox.active {
  background: #155724;
  border-color: #28a745;
}

.skip-images-checkbox.active:hover {
  background: #124a1e;
}

.skip-images-checkbox input[type="checkbox"] {
  width: 20px;
  height: 20px;
  margin-top: 2px;
  accent-color: #28a745;
  cursor: pointer;
}

.skip-images-content {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.skip-images-label {
  font-weight: 600;
  color: #fff;
  font-size: 0.95rem;
}

.skip-images-hint {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.8);
  margin-top: 2px;
}

.skip-images-spinner {
  color: #fff;
  font-size: 1rem;
}

.skip-images-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: #155724;
  border: 2px solid #28a745;
  border-radius: 8px;
  margin-bottom: 1rem;
  color: #fff;
  font-size: 0.9rem;
}

.skip-images-info i {
  color: #28a745;
}
</style>